import { BillViewPreference } from './Bill';
import { Jurisdiction } from './Jurisdiction';
import { AccountType, Organization } from './Organization';
import { PolicyArea } from './PolicyArea';
import { TeamMembership } from './Team';

export enum UserRole {
  OrgUser = 'org-user',
  OrgAdmin = 'org-admin',
  EnviewAdmin = 'enview-admin',
}

export enum ReasonForUse {
  PERSONAL = 'personal',
  PROFESSIONAL = 'professional',
}

export interface OrganizationUserRole {
  id: number;
  name: UserRole;
  organizationId: number;
  displayName: string;
  description: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface UserRegistration {
  id: number;
  organizationUserId: number;
  helpLevelNeeded: string;
  legislativeWorkExperience: string;
  additionalData: { reasonsForUsingEnview: string[] };
  createdAt: Date;
  updatedAt: Date;
}

interface MfaRegistration {
  id: number;
  type: string;
  info: string;
  userId: number;
  createdAt: Date;
  updatedAt: Date;
}

export enum EmailNotifications {
  BILL_REAL_TIME = 'realTimeBillNotification',
  BILL_DAILY = 'dailyBillNotification',
  BILL_WEEKLY = 'weeklyBillNotification',
  COMMITTEE_BILL_REAL_TIME = 'realTimeCommitteeBillNotification',
  COMMITTEE_BILL_DAILY = 'dailyCommitteeBillNotification',
  COMMITTEE_BILL_WEEKLY = 'weeklyCommitteeBillNotification',
  SEARCH_HOURLY = 'hourlySearchNotification',
  SEARCH_DAILY = 'dailySearchNotification',
  SEARCH_WEEKLY = 'weeklySearchNotification',
  BILL_RECOMMENDED = 'recommendedWeeklyBillNotification',
  COLLABORATION = 'collaborationNotification',
  MENTION = 'mentionNotification',
}

export enum EmailNotificationCategories {
  TRACKED_BILL_ACTIVITY = 'trackedBillActivity',
  TRACKED_BILL_COMMITTEE_EVENTS = 'trackedBillCommitteeEvents',
  SAVED_SEARCH = 'savedSearch',
  COLLABORATION = 'collaboration',
}

export type EmailNotificationFlags = {
  [K in EmailNotifications]: boolean;
};

/** Filters last applied on the universal Hearings page. Dates use the
 * `YYYY-MM-DD` format produced by use-query-params' `DateParam`. */
export interface HearingsFilterPreference {
  jurisdictions?: string[];
  sessions?: string[];
  cmteNames?: string;
  workspaces?: string[];
  workspaceFilterMode?: string;
  startDateRange?: string;
  endDateRange?: string;
}

/** Filters last applied on the universal Legislators page */
export interface LegislatorsFilterPreference {
  jurisdictions?: string[];
  party?: string[];
  chamber?: string[];
  committees?: string[];
}

export interface PageFilterPreferences {
  hearings?: HearingsFilterPreference;
  legislators?: LegislatorsFilterPreference;
}

export interface UserSettings extends EmailNotificationFlags {
  id?: number;
  organizationUserId?: number;
  timezone?: string;
  billViewPreference?: BillViewPreference;
  preferredWorkspaces?: Record<number, number | null>;
  /** View preferences such as saved page filters, keyed by organization ID so
   * each org a user switches into keeps its own jurisdictions, workspaces, etc. */
  viewPreferences?: Record<number, PageFilterPreferences>;
  createdAt?: Date;
  updatedAt?: Date;
}

export type OrganizationUser = {
  id: number;
  teamId: number;
  firstName: string;
  lastName: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  city: string;
  state: string;
  phoneNumber: string;
  suspended: boolean;
  orgSuspensions: { organizationId: number }[];
  jobTitle: string;
  organizationType: string;
  organizations: Organization[];
  roles: OrganizationUserRole[];
  policyAreas: PolicyArea[];
  teamMemberships: TeamMembership[];
  mfaRegistrations?: MfaRegistration[];
  settings: UserSettings;
  userRegistration: UserRegistration;
  credentialId?: number;
  disableSearchTip?: boolean;
  mfaEnabled?: boolean;
  isNew?: boolean;
  password?: string;
  searchJurisdictions?: Jurisdiction[];
  reasonForUse: ReasonForUse;
  adminSetFields?: AdminSetFields;
  googleSsoId: string;
  oidcSsoId: string;
};

export type ChangePasswordRequest = {
  firstName: string;
  lastName: string;
  email: string;
  currentPassword: string;
  newPassword: string;
  passwordConfirm: string;
};

export type UserInvitation = {
  id?: number;
  email: string;
  firstName?: string;
  lastName?: string;
  used?: boolean;
  url?: string;
  expiration: string;
  confirmationCode?: string;

  organizationId: number;
  authorizingUserId: number;

  org?: Organization;
  createdAt: string;
  status?: string;

  accountType: AccountType;
};

export type SignupData = {
  authorizationCode: string;
  accountType: AccountType;
  firstName: string;
  lastName: string;
  email: string;
  city?: string;
  state?: string;
  phoneNumber?: string;
  reasonForUse?: ReasonForUse;
  organizationId: number;
  organizationName?: string;
  teamId: number;
  password: string;
  passwordConfirm: string;
  termsAccepted: boolean;
  googleSsoId?: string;
};

// Plural admin defined/set fields for internal use only
export type AdminSetFields = {
  reasonForUse: ReasonForUse | null;
};

export type SubscribedTeamsForUser = {
  userId: number;
  email: string;
  subscribedTeamIds: number[];
};

export type UsersTrackingBill = {
  id: number;
  email: string;
  real_time_bill_notification: boolean;
  daily_bill_notification: boolean;
  weekly_bill_notification: boolean;
  real_time_committee_bill_notification: boolean;
  daily_committee_bill_notification: boolean;
  weekly_committee_bill_notification: boolean;
};
